import * as React from 'react';
import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Title } from 'react-admin';
import {
    Box, Card, Typography, IconButton, Chip, Stack, Avatar,
    TextField, Button, CircularProgress, Alert, Divider,
    Tooltip, Paper, Dialog, DialogTitle, DialogContent,
    DialogContentText, DialogActions, useTheme,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SendIcon from '@mui/icons-material/Send';
import DoNotDisturbIcon from '@mui/icons-material/DoNotDisturb';
import PersonIcon from '@mui/icons-material/Person';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import { API_URL, AGENT_SECRET } from './config';

const fmtTime = (d) =>
    new Date(d).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });

const fmtDay = (d) =>
    new Date(d).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });

const isImageAttachment = (msg) =>
    msg.attachment_type?.startsWith('image/') ||
    /\.(jpe?g|png|gif|webp|svg)(\?.*)?$/i.test(msg.attachment_url || '');

export default function ChatRoom() {
    const { chatId } = useParams();
    const navigate = useNavigate();
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';

    const [messages,    setMessages]    = useState([]);
    const [chatName,    setChatName]    = useState('');
    const [chatStatus,  setChatStatus]  = useState('open');
    const [userEmail,   setUserEmail]   = useState('');
    const [loading,     setLoading]     = useState(true);
    const [error,       setError]       = useState(null);
    const [inputText,   setInputText]   = useState('');
    const [sending,     setSending]     = useState(false);
    const [closing,     setClosing]     = useState(false);
    const [sendError,   setSendError]   = useState(null);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [userUsername, setUserUsername] = useState('');

    const bottomRef = useRef(null);
    const pollRef   = useRef(null);

    const markAsRead = useCallback(async () => {
        try {
            await fetch(`${API_URL}/adminMarkChatRead`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ agentSecret: AGENT_SECRET, chatId }),
            });
        } catch (e) {
            console.error('markAsRead error:', e);
        }
    }, [chatId]);

    const fetchMessages = useCallback(async (silent = false) => {
        try {
            const res = await fetch(`${API_URL}/adminGetChatMessages`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ agentSecret: AGENT_SECRET, chatId }),
            });
            const data = await res.json();
            if (!data.success) {
                if (!silent) throw new Error(data.message || 'Failed to load');
                return;
            }
            setChatName(data.chatName || chatId || '');
            setChatStatus(data.chatStatus || 'open');
            setUserEmail(data.userEmail || '');
            setMessages(data.messages || []);
            setUserUsername(data.userUsername || '');
        } catch (e) {
            if (!silent) setError(e.message);
        } finally {
            if (!silent) setLoading(false);
        }
    }, [chatId]);

    useEffect(() => {
        fetchMessages().then(() => markAsRead());
        pollRef.current = setInterval(() => fetchMessages(true), 5000);
        return () => { if (pollRef.current) clearInterval(pollRef.current); };
    }, [fetchMessages, markAsRead]);

    useEffect(() => {
        bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    const handleSend = async () => {
        const text = inputText.trim();
        if (!text || sending || chatStatus === 'closed') return;
        setSending(true);
        setSendError(null);
        try {
            const res = await fetch(`${API_URL}/sendAgentMessage`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ agentSecret: AGENT_SECRET, chatId, content: text }),
            });
            const data = await res.json();
            if (!data.success) throw new Error(data.message || 'Failed to send');
            setInputText('');
            await fetchMessages(true);
        } catch (e) {
            setSendError(e.message);
        } finally {
            setSending(false);
        }
    };

    const handleClose = async () => {
        setConfirmOpen(false);
        setClosing(true);
        try {
            const res = await fetch(`${API_URL}/adminCloseChat`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ agentSecret: AGENT_SECRET, chatId }),
            });
            const data = await res.json();
            if (data.success) setChatStatus('closed');
        } catch (e) {
            console.error('Close error:', e);
        } finally {
            setClosing(false);
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
    };

    const displayItems = [];
    let lastDate = null;
    for (const msg of messages) {
        const d = fmtDay(msg.created_at);
        if (d !== lastDate) { displayItems.push({ _sep: true, date: d, id: `sep-${d}-${msg.message_id}` }); lastDate = d; }
        displayItems.push(msg);
    }

    if (loading) return (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
            <CircularProgress />
        </Box>
    );

    if (error) return (
        <Box p={3}>
            <Alert severity="error" action={
                <Button size="small" onClick={() => fetchMessages()}>Retry</Button>
            }>{error}</Alert>
        </Box>
    );

    return (
        <Box sx={{ p: 3, width: '100%' }}>
            <Title title={`Chat: ${chatName}`} />

            <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
                <DialogTitle>Close conversation?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        The user will no longer be able to send messages.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
                    <Button onClick={handleClose} color="error" variant="contained">Close Chat</Button>
                </DialogActions>
            </Dialog>

            <Stack direction="row" alignItems="center" spacing={2} mb={2}>
                <IconButton onClick={() => navigate('/chats')} size="small">
                    <ArrowBackIcon />
                </IconButton>
                <Box flex={1}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <Typography variant="h6" fontWeight={700}>{chatName}</Typography>
                        <Chip
                            label={chatStatus}
                            size="small"
                            color={chatStatus === 'open' ? 'success' : 'default'}
                            variant="outlined"
                            sx={{ textTransform: 'capitalize', fontSize: 11 }}
                        />
                    </Stack>
                    {(userUsername || userEmail) && (
                        <Typography variant="caption" color="text.secondary">
                            {userUsername ? `@${userUsername}` : ''}{userUsername && userEmail ? ' · ' : ''}{userEmail}
                        </Typography>
                    )}
                </Box>
                {chatStatus === 'open' && (
                    <Tooltip title="Close conversation">
                        <Button
                            size="small"
                            variant="outlined"
                            color="error"
                            startIcon={<DoNotDisturbIcon />}
                            onClick={() => setConfirmOpen(true)}
                            disabled={closing}
                        >
                            {closing ? 'Closing…' : 'Close Chat'}
                        </Button>
                    </Tooltip>
                )}
            </Stack>

            <Card sx={{ borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,.08)', mb: 2 }}>
                <Box sx={{
                    height: 480, overflowY: 'auto', p: 2,
                    display: 'flex', flexDirection: 'column', gap: 0.5,
                    bgcolor: isDark ? 'grey.900' : '#f8f9fa',
                }}>
                    {messages.length === 0 && (
                        <Box textAlign="center" color="text.secondary" mt={8}>
                            <Typography>No messages yet</Typography>
                        </Box>
                    )}
                    {displayItems.map((item) => {
                        if (item._sep) {
                            return (
                                <Box key={item.id} display="flex" alignItems="center" my={1.5}>
                                    <Divider sx={{ flex: 1 }} />
                                    <Typography variant="caption" color="text.secondary" sx={{ mx: 1.5, whiteSpace: 'nowrap' }}>
                                        {item.date}
                                    </Typography>
                                    <Divider sx={{ flex: 1 }} />
                                </Box>
                            );
                        }
                        const isAgent = item.sender_type === 'agent';
                        const hasImage = item.attachment_url && isImageAttachment(item);
                        const hasFile  = item.attachment_url && !hasImage;
                        return (
                            <Box key={item.message_id} display="flex" justifyContent={isAgent ? 'flex-end' : 'flex-start'} alignItems="flex-end" gap={1} mb={0.5}>
                                {!isAgent && (
                                    <Avatar sx={{ width: 28, height: 28, bgcolor: '#e0e7ff', mb: 0.5 }}>
                                        <PersonIcon sx={{ fontSize: 16, color: '#6366f1' }} />
                                    </Avatar>
                                )}
                                <Box maxWidth="70%">
                                    <Paper elevation={0} sx={{
                                        p: '8px 12px',
                                        borderRadius: isAgent ? '12px 12px 2px 12px' : '12px 12px 12px 2px',
                                        bgcolor: isAgent ? '#6366f1' : (isDark ? 'grey.800' : '#ffffff'),
                                        color: isAgent ? '#ffffff' : 'text.primary',
                                        boxShadow: '0 1px 3px rgba(0,0,0,.1)',
                                    }}>
                                        {item.content ? (
                                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                                {item.content}
                                            </Typography>
                                        ) : null}
                                        {hasImage && (
                                            <Box mt={item.content ? 1 : 0}>
                                                <a href={item.attachment_url} target="_blank" rel="noopener noreferrer">
                                                    <img
                                                        src={item.attachment_url}
                                                        alt={item.attachment_name || 'attachment'}
                                                        style={{ maxWidth: '100%', maxHeight: 240, borderRadius: 8, display: 'block', cursor: 'pointer' }}
                                                    />
                                                </a>
                                            </Box>
                                        )}
                                        {hasFile && (
                                            <Box mt={item.content ? 1 : 0}>
                                                <a
                                                    href={item.attachment_url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    style={{ display: 'flex', alignItems: 'center', gap: 6, color: isAgent ? '#ffffff' : '#6366f1', textDecoration: 'none' }}
                                                >
                                                    <AttachFileIcon sx={{ fontSize: 16 }} />
                                                    <Typography variant="body2" sx={{ wordBreak: 'break-all', textDecoration: 'underline' }}>
                                                        {item.attachment_name || 'File'}
                                                    </Typography>
                                                </a>
                                            </Box>
                                        )}
                                    </Paper>
                                    <Typography variant="caption" color="text.disabled" display="block" textAlign={isAgent ? 'right' : 'left'} mt={0.25}>
                                        {isAgent ? '🛡 Support' : '👤 User'} · {fmtTime(item.created_at)}
                                    </Typography>
                                </Box>
                                {isAgent && (
                                    <Avatar sx={{ width: 28, height: 28, bgcolor: '#6366f1', mb: 0.5 }}>
                                        <SupportAgentIcon sx={{ fontSize: 16 }} />
                                    </Avatar>
                                )}
                            </Box>
                        );
                    })}
                    <div ref={bottomRef} />
                </Box>

                <Divider />
                {chatStatus === 'open' ? (
                    <Box p={2}>
                        {sendError && (
                            <Alert severity="error" sx={{ mb: 1 }} onClose={() => setSendError(null)}>{sendError}</Alert>
                        )}
                        <Stack direction="row" spacing={1} alignItems="flex-end">
                            <TextField
                                multiline
                                maxRows={4}
                                fullWidth
                                size="small"
                                placeholder="Type a reply… (Enter to send, Shift+Enter for new line)"
                                value={inputText}
                                onChange={e => setInputText(e.target.value)}
                                onKeyDown={handleKeyDown}
                                disabled={sending}
                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                            />
                            <Button
                                variant="contained"
                                onClick={handleSend}
                                disabled={sending || !inputText.trim()}
                                sx={{ minWidth: 48, height: 40, bgcolor: '#6366f1', '&:hover': { bgcolor: '#4f46e5' }, borderRadius: 2 }}
                            >
                                {sending ? <CircularProgress size={18} color="inherit" /> : <SendIcon fontSize="small" />}
                            </Button>
                        </Stack>
                        <Typography variant="caption" color="text.secondary" mt={0.5} display="block">
                            Auto-refreshes every 5s · {messages.length} message{messages.length !== 1 ? 's' : ''}
                        </Typography>
                    </Box>
                ) : (
                    <Box p={2} textAlign="center">
                        <Typography variant="body2" color="text.secondary">This conversation is closed.</Typography>
                    </Box>
                )}
            </Card>
        </Box>
    );
}